import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input } from '@/components/ui';
import {
  vendorApproveSchema,
  type VendorApproveValues,
} from '../schemas/vendorReviewSchema';

export interface VendorApproveModalProps {
  isOpen: boolean;
  applicationName: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (values: VendorApproveValues) => void;
  onCancel: () => void;
}

export const VendorApproveModal = ({
  isOpen,
  applicationName,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: VendorApproveModalProps) => {
  const { t } = useTranslation('vendors');
  const form = useForm<VendorApproveValues>({
    resolver: zodResolver(vendorApproveSchema),
    defaultValues: { reason: '' },
    mode: 'onBlur',
  });
  const reasonRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      form.reset({ reason: '' });
      requestAnimationFrame(() => reasonRef.current?.focus());
    }
  }, [isOpen, form]);

  const { ref: registerRef, ...reasonRegister } = form.register('reason');

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent data-testid="vendor-approve-modal" className="max-w-md">
        <DialogTitle>{t('vendors.actions.approve')}</DialogTitle>
        <DialogDescription>
          {t('vendors.actionModal.subject', { vendorName: applicationName })}
        </DialogDescription>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="vendor-approve-reason"
              className="text-sm text-[color:var(--content-muted)]"
            >
              {t('vendors.actionModal.reasonOptional')}
            </label>
            <Input
              id="vendor-approve-reason"
              {...reasonRegister}
              ref={(el) => {
                registerRef(el);
                reasonRef.current = el;
              }}
              maxLength={1000}
              style={{ height: 'var(--density-compact-form-h)' }}
            />
          </div>

          {errorMessage ? (
            <p
              role="alert"
              aria-live="polite"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('vendors.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('vendors.actionModal.submitting')
                : t('vendors.actions.approveConfirm')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
